import React, { useCallback } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import "./PaymentSuccess.css";
import { auth } from "../firebase/config";
import { setPageMeta } from "../utils/seo";

const PaymentSuccess = ({ clearCart }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { paymentId, order, userName } = location.state || {};

  const [shipmentStatus, setShipmentStatus] = React.useState(null);
  const [shipmentError, setShipmentError] = React.useState(null);
  const [shipmentAttempted, setShipmentAttempted] = React.useState(false);
  const [canRetry, setCanRetry] = React.useState(false);

  React.useEffect(() => {
    setPageMeta({
      title: "Payment Success | Banat India",
      description:
        "Your payment was successful. View order details and next steps.",
      keywords: "payment success, order confirmation, Banat India order",
    });
  }, []);

  // Function to update existing order in Firebase with Shiprocket details
  const updateOrderWithShiprocketDetails = useCallback(async (shiprocketInfo) => {
    try {
      // Extract order and shipment IDs from different possible field names
      const orderId = shiprocketInfo.order_id || shiprocketInfo.orderId || shiprocketInfo.id || shiprocketInfo.orderid;
      const shipmentId = shiprocketInfo.shipment_id || shiprocketInfo.shipmentId || shiprocketInfo.shipmentid;
      
      if (auth.currentUser?.uid) {
        const { ref, get, update } = await import("firebase/database");
        const { realtimeDb } = await import("../firebase/config");
        
        // Find the existing order with the same paymentId
        const ordersRef = ref(realtimeDb, `orders/${auth.currentUser.uid}`);
        const snapshot = await get(ordersRef);
        
        if (snapshot.exists()) {
          const orders = snapshot.val();
          let orderKey = null;
          
          // Find the order with matching paymentId
          for (const key in orders) {
            if (orders[key].paymentId === paymentId) {
              orderKey = key;
              break;
            }
          }
          
          if (orderKey) {
            // Update the existing order with Shiprocket details
            const updateData = {
              shiprocket_order_id: orderId,
              shiprocket_shipment_id: shipmentId,
              shiprocket_status: "Pending",
              shiprocket_courier: shiprocketInfo.courier_name || shiprocketInfo.courier || null,
              shiprocket_awb: shiprocketInfo.awb || null,
              shiprocket_tracking_url: shiprocketInfo.tracking_url || null,
              shiprocket_pickup_location: shiprocketInfo.pickup_location || "warehouse"
            };
            
            await update(ref(realtimeDb, `orders/${auth.currentUser.uid}/${orderKey}`), updateData);
            console.log("Order updated in Realtime Database with Shiprocket details:", orderKey);
          } else {
            console.log("No existing order found with paymentId:", paymentId);
          }
        } else {
          console.log("No orders found for user");
        }
      } else {
        console.log("User not authenticated, skipping Firebase update");
      }
      
    } catch (error) {
      console.error("Error updating order in Firebase:", error);
    }
  }, [paymentId]);

  // Function to fetch and update latest order details including AWB
  const fetchAndUpdateOrderDetails = useCallback(async (orderId) => {
    try {
      console.log('Fetching latest order details for:', orderId);
      
      // Get the shipment ID from Firebase
      if (auth.currentUser?.uid) {
        const { ref, get } = await import("firebase/database");
        const { realtimeDb } = await import("../firebase/config");
        
        // Find the existing order with the same paymentId
        const ordersRef = ref(realtimeDb, `orders/${auth.currentUser.uid}`);
        const snapshot = await get(ordersRef);
        
        if (snapshot.exists()) {
          const orders = snapshot.val();
          let orderKey = null;
          let shipmentId = null;
          
          // Find the order with matching paymentId
          for (const key in orders) {
            if (orders[key].paymentId === paymentId) {
              orderKey = key;
              shipmentId = orders[key].shiprocket_shipment_id;
              break;
            }
          }
          
          if (orderKey && shipmentId) {
            // Fetch tracking details directly
            const response = await fetch(`https://shiprocket-backend-maeh.onrender.com/track-shipment/${shipmentId}`);
            const result = await response.json();
            
            if (result.success && result.data) {
              console.log('Latest tracking details:', result.data);
              
              // Extract AWB from tracking data
              const shipmentTrack = result.data.tracking_data?.shipment_track?.[0];
              const awbNumber = shipmentTrack?.awb_code;
              const courierName = shipmentTrack?.courier_name;
              const status = shipmentTrack?.current_status;
              
              console.log('Extracted AWB:', awbNumber);
              console.log('Extracted Courier:', courierName);
              console.log('Extracted Status:', status);
              
              if (awbNumber) {
                // Update Firebase with latest details
                const { update } = await import("firebase/database");
                
                const updateData = {
                  shiprocket_awb: awbNumber,
                  shiprocket_courier: courierName,
                  shiprocket_status: status
                };
                
                await update(ref(realtimeDb, `orders/${auth.currentUser.uid}/${orderKey}`), updateData);
                console.log("Order updated with AWB number:", awbNumber);
                
                // Show success message
                setShipmentStatus(`✅ Order updated! AWB Number: ${awbNumber}\nCourier: ${courierName}\nStatus: ${status}`);
              } else {
                setShipmentError('AWB number not yet generated. Please try again later.');
              }
            } else {
              console.log('Failed to fetch tracking details:', result.error);
              setShipmentError(`Failed to fetch tracking details: ${result.error}`);
            }
          } else {
            setShipmentError('No shipment ID found for this order. Please wait for shipment creation.');
          }
        } else {
          setShipmentError('No orders found for user');
        }
      } else {
        setShipmentError('User not authenticated');
      }
    } catch (error) {
      console.error("Error fetching order details:", error);
      setShipmentError(`Error fetching order details: ${error.message}`);
    }
  }, [paymentId]);

  // Check if Shiprocket order already exists for this payment
  const checkExistingShiprocketOrder = useCallback(async () => {
    try {
      if (!auth.currentUser?.uid || !paymentId) return false;
      
      const { ref, get } = await import("firebase/database");
      const { realtimeDb } = await import("../firebase/config");
      
      const ordersRef = ref(realtimeDb, `orders/${auth.currentUser.uid}`);
      const snapshot = await get(ordersRef);
      
      if (snapshot.exists()) {
        const orders = snapshot.val();
        // Check if any order with this paymentId already has Shiprocket details
        for (const key in orders) {
          const order = orders[key];
          if (order.paymentId === paymentId && order.shiprocket_order_id) {
            console.log('Found existing Shiprocket order:', order.shiprocket_order_id);
            return true;
          }
        }
      }
      return false;
    } catch (error) {
      console.error("Error checking existing Shiprocket order:", error);
      return false;
    }
  }, [paymentId]);

  // Function to check if shipment was actually created despite the message
  const checkShipmentStatus = useCallback(async () => {
    try {
      if (!auth.currentUser?.uid || !paymentId) return;
      
      const { ref, get } = await import("firebase/database");
      const { realtimeDb } = await import("../firebase/config");
      
      const ordersRef = ref(realtimeDb, `orders/${auth.currentUser.uid}`);
      const snapshot = await get(ordersRef);
      
      if (snapshot.exists()) {
        const orders = snapshot.val();
        // Check if any order with this paymentId has Shiprocket details
        for (const key in orders) {
          const order = orders[key];
          if (order.paymentId === paymentId && order.shiprocket_order_id) {
            console.log('Found existing Shiprocket order:', order.shiprocket_order_id);
            setShipmentStatus(
              `✅ Shipment was successfully created! 
              Shiprocket Order ID: ${order.shiprocket_order_id}`
            );
            return true;
          }
        }
      }
      return false;
    } catch (error) {
      console.error("Error checking shipment status:", error);
      return false;
    }
  }, [paymentId]);

  // Function to retry shipment creation
  const retryShipmentCreation = useCallback(async () => {
    try {
      // Clear the attempt flag
      const shipmentAttemptKey = `shipment_attempt_${paymentId}`;
      localStorage.removeItem(shipmentAttemptKey);
      
      // Reset states
      setShipmentAttempted(false);
      setCanRetry(false);
      setShipmentStatus(null);
      setShipmentError(null);
      
      // Trigger the shipment creation process again
      if (order && order.shipping && order.items) {
        setShipmentAttempted(true);
        
        // Mark that we've attempted to create a shipment for this payment
        localStorage.setItem(shipmentAttemptKey, Date.now().toString());
        
        // Add a small delay to prevent rapid requests
        setTimeout(async () => {
          // Double-check if Shiprocket order already exists in Firebase
          const orderExists = await checkExistingShiprocketOrder();
          if (orderExists) {
            console.log('Shiprocket order already exists for this payment, skipping creation');
            setShipmentStatus('Shiprocket order already exists for this payment');
            return;
          }
          
          // Re-run the shipment creation logic
          const createShipment = async () => {
            try {
              // Ensure all required fields are present
              const requiredFields = {
                name: order?.shipping?.name,
                address: order?.shipping?.address,
                city: order?.shipping?.city,
                pincode: order?.shipping?.pincode,
                state: order?.shipping?.state,
                email: order?.shipping?.email,
                phone: order?.shipping?.phone
              };
              
              // Fix city/state parsing if they're the same
              if (requiredFields.city === requiredFields.state) {
                if (requiredFields.state && requiredFields.state.toLowerCase() === 'hyderabad') {
                  requiredFields.state = 'Telangana';
                }
              }
              
              // Validate required fields
              if (!requiredFields.name || requiredFields.name.trim() === '') {
                setShipmentError('Customer name is required');
                return;
              }
              
              if (!requiredFields.address || requiredFields.address.trim() === '') {
                setShipmentError('Address is required');
                return;
              }
              
              if (!requiredFields.city || requiredFields.city.trim() === '') {
                setShipmentError('City is required');
                return;
              }
              
              if (!requiredFields.pincode || requiredFields.pincode.trim() === '') {
                setShipmentError('Pincode is required');
                return;
              }
              
              if (!requiredFields.state || requiredFields.state.trim() === '') {
                setShipmentError('State is required');
                return;
              }
              
              if (!requiredFields.email || requiredFields.email.trim() === '') {
                setShipmentError('Email is required');
                return;
              }
              
              if (!requiredFields.phone || requiredFields.phone.trim() === '') {
                setShipmentError('Phone number is required');
                return;
              }
              
              // Generate unique order ID with timestamp and random string
              const generateOrderId = () => `SR_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
              
              const shiprocketOrderData = {
                order_id: generateOrderId(),
                order_date: new Date().toISOString().split('T')[0],
                pickup_location: "warehouse",
                channel_id: "0",
                billing_customer_name: requiredFields.name,
                billing_last_name: order?.shipping?.lastName || "",
                billing_address: requiredFields.address,
                billing_city: requiredFields.city,
                billing_pincode: requiredFields.pincode,
                billing_state: requiredFields.state,
                billing_country: order?.shipping?.country || "India",
                billing_email: requiredFields.email,
                billing_phone: requiredFields.phone,
                shipping_is_billing: true,
                shipping_customer_name: requiredFields.name,
                shipping_last_name: order?.shipping?.lastName || "",
                shipping_address: requiredFields.address,
                shipping_city: requiredFields.city,
                shipping_pincode: requiredFields.pincode,
                shipping_state: requiredFields.state,
                shipping_country: order?.shipping?.country || "India",
                shipping_email: requiredFields.email,
                shipping_phone: requiredFields.phone,
                order_items: order.items.map(item => ({
                  name: item.name || "Product",
                  sku: item.sku || item.id || "SKU",
                  units: parseInt(item.quantity) || 1,
                  selling_price: parseFloat(item.price) || 0,
                  discount: 0,
                  tax: 0,
                  hsn: 0
                })),
                payment_method: "Prepaid",
                sub_total: parseFloat(order.total) || 0,
                length: 10,
                breadth: 10,
                height: 5,
                weight: 1.5
              };
              
              // Call Express backend
              let response, result;
              let retryCount = 0;
              const maxRetries = 3;
              
              while (retryCount < maxRetries) {
                try {
                  response = await fetch('https://shiprocket-backend-maeh.onrender.com/create-shipment', {
                    method: 'POST',
                    headers: {
                      'Content-Type': 'application/json',
                    },
                    body: JSON.stringify(shiprocketOrderData)
                  });

                  result = await response.json();
                  
                  if (!result.success && result.error && result.error.includes('order already exists')) {
                    retryCount++;
                    if (retryCount < maxRetries) {
                      console.log(`Order ID ${shiprocketOrderData.order_id} already exists, retrying with new ID...`);
                      shiprocketOrderData.order_id = generateOrderId();
                      continue;
                    }
                  }
                  break;
                } catch (err) {
                  retryCount++;
                  if (retryCount >= maxRetries) {
                    throw err;
                  }
                  console.log(`Request failed, retrying... (${retryCount}/${maxRetries})`);
                }
              }
              
              if (result.success) {
                const shiprocketInfo = result.data;
                
                const orderId = shiprocketInfo.order_id || shiprocketInfo.orderId || shiprocketInfo.id || shiprocketInfo.orderid || 'N/A';
                const shipmentId = shiprocketInfo.shipment_id || shiprocketInfo.shipmentId || shiprocketInfo.shipmentid || 'N/A';
                
                setShipmentStatus(
                  `✅ Shipment created successfully! 
                  Shiprocket Order ID: ${orderId} | 
                  Shipment ID: ${shipmentId}`
                );
                
                await updateOrderWithShiprocketDetails(shiprocketInfo);
              } else {
                if (result.error && result.error.includes('429')) {
                  setShipmentError("Shiprocket API rate limit reached. Your order is confirmed, but shipment creation is temporarily unavailable. Please try again later or contact support.");
                } else {
                  setShipmentError("Shipment creation failed: " + result.error);
                }
              }
            } catch (err) {
              if (err.message && err.message.includes('429')) {
                setShipmentError("Shiprocket API rate limit reached. Your order is confirmed, but shipment creation is temporarily unavailable. Please try again later or contact support.");
              } else {
                setShipmentError("Shipment creation failed: " + (err.message || err));
              }
            }
          };
          createShipment();
        }, 1000);
      }
    } catch (error) {
      console.error('Error retrying shipment creation:', error);
      setShipmentError('Failed to retry shipment creation: ' + error.message);
    }
  }, [paymentId, order, checkExistingShiprocketOrder, updateOrderWithShiprocketDetails]);

  React.useEffect(() => {
    if (!paymentId) {
      navigate("/dashboard?tab=orders");
      return;
    }
    
    // Clear the cart after successful payment (only once)
    if (clearCart && !shipmentAttempted) {
      clearCart();
    }
    
    // Create Shiprocket order after payment success (only once)
    if (order && order.shipping && order.items && !shipmentAttempted) {
      setShipmentAttempted(true);
      
      // Check if order already has Shiprocket details to prevent duplicates
      if (order.shiprocket_order_id) {
        console.log('Order already has Shiprocket details, skipping creation:', order.shiprocket_order_id);
        setShipmentStatus(
          `Order already has Shiprocket details! 
          Shiprocket Order ID: ${order.shiprocket_order_id}`
        );
        return;
      }
      
      // Check if we've already attempted to create a shipment for this payment
      const shipmentAttemptKey = `shipment_attempt_${paymentId}`;
      const hasAttempted = localStorage.getItem(shipmentAttemptKey);
      
      if (hasAttempted) {
        console.log('Shipment creation already attempted for this payment, checking if it was successful...');
        
        // Check if shipment was actually created despite the attempt message
        checkShipmentStatus().then(wasCreated => {
          if (!wasCreated) {
            setShipmentStatus('Shipment creation already attempted for this payment. If you need to retry, clear localStorage and refresh.');
            setCanRetry(true);
          }
        });
        return;
      }
      
      // Mark that we've attempted to create a shipment for this payment
      localStorage.setItem(shipmentAttemptKey, Date.now().toString());
      
      // Add a small delay to prevent rapid requests
      setTimeout(async () => {
        // Double-check if Shiprocket order already exists in Firebase
        const orderExists = await checkExistingShiprocketOrder();
        if (orderExists) {
          console.log('Shiprocket order already exists for this payment, skipping creation');
          setShipmentStatus('Shiprocket order already exists for this payment');
          return;
        }
        
        const createShipment = async () => {
          try {
            // Ensure all required fields are present
            const requiredFields = {
              name: order?.shipping?.name,
              address: order?.shipping?.address,
              city: order?.shipping?.city,
              pincode: order?.shipping?.pincode,
              state: order?.shipping?.state,
              email: order?.shipping?.email,
              phone: order?.shipping?.phone
            };
            
            // Fix city/state parsing if they're the same
            if (requiredFields.city === requiredFields.state) {
              // If city and state are the same, use the state as state and city as city
              // This handles cases like "hyderabad, hyderabad, telangana"
              if (requiredFields.state && requiredFields.state.toLowerCase() === 'hyderabad') {
                requiredFields.state = 'Telangana';
              }
            }
            
            // Validate required fields
            if (!requiredFields.name || requiredFields.name.trim() === '') {
              setShipmentError('Customer name is required');
              return;
            }
            
            if (!requiredFields.address || requiredFields.address.trim() === '') {
              setShipmentError('Address is required');
              return;
            }
            
            if (!requiredFields.city || requiredFields.city.trim() === '') {
              setShipmentError('City is required');
              return;
            }
            
            if (!requiredFields.pincode || requiredFields.pincode.trim() === '') {
              setShipmentError('Pincode is required');
              return;
            }
            
            if (!requiredFields.state || requiredFields.state.trim() === '') {
              setShipmentError('State is required');
              return;
            }
            
            if (!requiredFields.email || requiredFields.email.trim() === '') {
              setShipmentError('Email is required');
              return;
            }
            
            if (!requiredFields.phone || requiredFields.phone.trim() === '') {
              setShipmentError('Phone number is required');
              return;
            }
            
            // Generate unique order ID with timestamp and random string
            const generateOrderId = () => `SR_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
            
            const shiprocketOrderData = {
              order_id: generateOrderId(),
              order_date: new Date().toISOString().split('T')[0],
              pickup_location: "warehouse", // Change this to match your Shiprocket pickup location name
              channel_id: "0",
              billing_customer_name: requiredFields.name,
              billing_last_name: order?.shipping?.lastName || "",
              billing_address: requiredFields.address,
              billing_city: requiredFields.city,
              billing_pincode: requiredFields.pincode,
              billing_state: requiredFields.state,
              billing_country: order?.shipping?.country || "India",
              billing_email: requiredFields.email,
              billing_phone: requiredFields.phone,
              shipping_is_billing: true,
              // Add shipping fields explicitly (even if same as billing)
              shipping_customer_name: requiredFields.name,
              shipping_last_name: order?.shipping?.lastName || "",
              shipping_address: requiredFields.address,
              shipping_city: requiredFields.city,
              shipping_pincode: requiredFields.pincode,
              shipping_state: requiredFields.state,
              shipping_country: order?.shipping?.country || "India",
              shipping_email: requiredFields.email,
              shipping_phone: requiredFields.phone,
              order_items: order.items.map(item => ({
                name: item.name || "Product",
                sku: item.sku || item.id || "SKU",
                units: parseInt(item.quantity) || 1,
                selling_price: parseFloat(item.price) || 0,
                discount: 0,
                tax: 0,
                hsn: 0
              })),
              payment_method: "Prepaid",
              sub_total: parseFloat(order.total) || 0,
              length: 10,
              breadth: 10,
              height: 5,
              weight: 1.5
            };
            

            
            // Validate the final order data structure
            const validationErrors = [];
            if (!shiprocketOrderData.billing_customer_name || shiprocketOrderData.billing_customer_name === 'N/A') {
              validationErrors.push('billing_customer_name');
            }
            if (!shiprocketOrderData.billing_address || shiprocketOrderData.billing_address === 'N/A') {
              validationErrors.push('billing_address');
            }
            if (!shiprocketOrderData.billing_city || shiprocketOrderData.billing_city === 'N/A') {
              validationErrors.push('billing_city');
            }
            if (!shiprocketOrderData.billing_pincode || shiprocketOrderData.billing_pincode === 'N/A') {
              validationErrors.push('billing_pincode');
            }
            if (!shiprocketOrderData.billing_state || shiprocketOrderData.billing_state === 'N/A') {
              validationErrors.push('billing_state');
            }
            if (!shiprocketOrderData.billing_email || shiprocketOrderData.billing_email === 'N/A') {
              validationErrors.push('billing_email');
            }
            if (!shiprocketOrderData.billing_phone || shiprocketOrderData.billing_phone === 'N/A') {
              validationErrors.push('billing_phone');
            }
            
            if (validationErrors.length > 0) {
              setShipmentError(`Invalid data in fields: ${validationErrors.join(', ')}`);
              return;
            }
            

            
            // Call Express backend instead of direct Shiprocket API
            let response, result;
            let retryCount = 0;
            const maxRetries = 3;
            
            while (retryCount < maxRetries) {
              try {
                response = await fetch('https://shiprocket-backend-maeh.onrender.com/create-shipment', {
                  method: 'POST',
                  headers: {
                    'Content-Type': 'application/json',
                  },
                  body: JSON.stringify(shiprocketOrderData)
                });

                result = await response.json();
                
                // If order already exists, generate new order ID and retry
                if (!result.success && result.error && result.error.includes('order already exists')) {
                  retryCount++;
                  if (retryCount < maxRetries) {
                    console.log(`Order ID ${shiprocketOrderData.order_id} already exists, retrying with new ID...`);
                    shiprocketOrderData.order_id = generateOrderId();
                    continue;
                  }
                }
                break;
              } catch (err) {
                retryCount++;
                if (retryCount >= maxRetries) {
                  throw err;
                }
                console.log(`Request failed, retrying... (${retryCount}/${maxRetries})`);
              }
            }

            
            if (result.success) {
              const shiprocketInfo = result.data;
              
              // Try different possible field names for order_id and shipment_id
              const orderId = shiprocketInfo.order_id || shiprocketInfo.orderId || shiprocketInfo.id || shiprocketInfo.orderid || 'N/A';
              const shipmentId = shiprocketInfo.shipment_id || shiprocketInfo.shipmentId || shiprocketInfo.shipmentid || 'N/A';
              
              setShipmentStatus(
                `Shipment created successfully! 
                Shiprocket Order ID: ${orderId} | 
                Shipment ID: ${shipmentId}`
              );
              
              // Update existing order in Firebase with Shiprocket details
              await updateOrderWithShiprocketDetails(shiprocketInfo);
            } else {
              if (result.error && result.error.includes('429')) {
                setShipmentError("Shiprocket API rate limit reached. Your order is confirmed, but shipment creation is temporarily unavailable. Please try again later or contact support.");
              } else {
                setShipmentError("Shipment creation failed: " + result.error);
              }
            }
          } catch (err) {
            if (err.message && err.message.includes('429')) {
              setShipmentError("Shiprocket API rate limit reached. Your order is confirmed, but shipment creation is temporarily unavailable. Please try again later or contact support.");
            } else {
              setShipmentError("Shipment creation failed: " + (err.message || err));
            }
          }
        };
        createShipment();
      }, 1000); // 1 second delay
    }
  }, [paymentId, navigate, clearCart, order, checkExistingShiprocketOrder, updateOrderWithShiprocketDetails, checkShipmentStatus, shipmentAttempted]);

  if (!paymentId) return null;

  const handlePrint = () => {
    window.print();
  };

  return (
    <div className="payment-success-container">
      <h2>Thank you{userName ? `, ${userName}` : ''}!</h2>
      <h3>Payment Successful</h3>
      <p className="success-message">Your payment was successful.</p>
      <p><strong>Payment ID:</strong> {paymentId}</p>
      <p style={{margin: '16px 0', color: '#555'}}>Estimated delivery: <strong>3-5 business days</strong></p>
      {order && (
        <div className="payment-success-summary">
          <h4>Order Summary</h4>
          <ul>
            {order.items && order.items.map((item, idx) => (
              <li key={item.id || idx}>
                <span style={{fontWeight: 500}}>{item.name}</span> x {item.quantity} — ₹{item.price * item.quantity}
              </li>
            ))}
          </ul>
          <div style={{marginTop: 12}}>
            <strong>Total Paid:</strong> ₹{order.total}
          </div>
          <div style={{marginTop: 12}}>
            <strong>Shipping to:</strong><br/>
            {order.shipping && (
              <span>
                {order.shipping.name}<br/>
                {order.shipping.address}, {order.shipping.city}, {order.shipping.state} - {order.shipping.pincode}<br/>
                {order.shipping.phone}<br/>
                {order.shipping.email}
              </span>
            )}
          </div>
        </div>
      )}
      <div className="refund-policy-note">
        <p><strong>⚠️ Important:</strong> We do not provide refunds or cancellations for orders. Please review your order details carefully.</p>
      </div>
      
      {/* Button to fetch latest order details including AWB */}
      <div style={{ margin: '16px 0', textAlign: 'center' }}>
        <button 
          onClick={() => {
            // Get the order ID from Firebase or use a default
            const orderId = "912017231"; // You can replace this with the actual order ID
            fetchAndUpdateOrderDetails(orderId);
          }}
          style={{
            backgroundColor: '#17a2b8',
            color: 'white',
            border: 'none',
            padding: '10px 20px',
            borderRadius: '5px',
            cursor: 'pointer',
            fontSize: '14px',
            marginBottom: '10px'
          }}
        >
          🔄 Fetch Latest Order Details (AWB)
        </button>
        <small style={{ display: 'block', color: '#666' }}>
          Click to fetch the latest AWB number and tracking details from Shiprocket
        </small>
      </div>
      
      {shipmentStatus && (
        <div style={{ color: 'green', margin: '16px 0', fontWeight: 500 }}>
          {shipmentStatus}
        </div>
      )}
      {shipmentError && (
        <div style={{ color: 'red', margin: '16px 0', fontWeight: 500 }}>
          {shipmentError}
          <br />
          <small style={{ color: 'blue' }}>
            DEBUG: Name={order?.shipping?.name}, Address={order?.shipping?.address}, City={order?.shipping?.city}, State={order?.shipping?.state}
          </small>
        </div>
      )}
      {canRetry && (
        <div style={{ margin: '16px 0' }}>
          <button 
            onClick={retryShipmentCreation}
            style={{
              backgroundColor: '#007bff',
              color: 'white',
              border: 'none',
              padding: '10px 20px',
              borderRadius: '5px',
              cursor: 'pointer',
              fontSize: '14px',
              marginRight: '10px'
            }}
          >
            🔄 Retry Shipment Creation
          </button>
          <button 
            onClick={() => {
              const shipmentAttemptKey = `shipment_attempt_${paymentId}`;
              localStorage.removeItem(shipmentAttemptKey);
              window.location.reload();
            }}
            style={{
              backgroundColor: '#28a745',
              color: 'white',
              border: 'none',
              padding: '10px 20px',
              borderRadius: '5px',
              cursor: 'pointer',
              fontSize: '14px'
            }}
          >
            🗑️ Clear & Reload
          </button>
          <small style={{ display: 'block', marginTop: '8px', color: '#666' }}>
            Left button: Retry shipment creation | Right button: Clear localStorage and reload page
          </small>
        </div>
      )}
      <div className="payment-success-actions">
        <button onClick={handlePrint}>Print Receipt</button>
        <button onClick={() => navigate("/shop") }>Continue Shopping</button>
        <button onClick={() => navigate("/dashboard?tab=orders")}>Go to Orders</button>
      </div>
    </div>
  );
};

export default PaymentSuccess; 